/** Герои, навыки, усиления, Tech. */
import { AttackDef, CostCurve, StatModifier } from "./core";
import { ContentId, HeroId, I18nKey, PosInt, UInt } from "../common";
import { z } from "zod";

/** Герой. */
export const HeroDef = z.object({
  id: HeroId,
  name: I18nKey,
  /** Волна, на которой герой открывается (1, 5, 10, 20, 30). */
  unlockWave: PosInt,
  attack: AttackDef,
  /** Активный навык героя (ровно один, грилл 2026-10-24). */
  skillId: ContentId,
  /** Прирост урона за уровень, промилле от базового. */
  damagePerLevelPermille: UInt,
  /** Цена уровня (вкладка Level). */
  levelCost: CostCurve,
  /** Максимальный уровень. */
  maxLevel: PosInt,
});
export type HeroDef = z.infer<typeof HeroDef>;

/** Тип действия навыка — по нему engine выбирает реализацию (E3). */
export const SkillKind = z.enum(["barrage", "airstrike", "chainLightning", "firewall", "freeze"]);
export type SkillKind = z.infer<typeof SkillKind>;

/** Активный навык героя. */
export const SkillDef = z.object({
  id: ContentId,
  heroId: HeroId,
  name: I18nKey,
  description: I18nKey,
  kind: SkillKind,
  /** Перезарядка, тиков. */
  cooldownTicks: PosInt,
  /** Длительность действия, тиков (0 — мгновенно). */
  durationTicks: UInt,
  /** Числовые параметры реализации (`damage`, `radius`, `targets`, `slowPermille`…) — целые. */
  params: z.record(z.string(), z.number().int()),
  /** Что даёт каждая звезда навыку (звезда k → элемент k−1). */
  starUpgrades: z.array(z.array(StatModifier)).max(5),
});
export type SkillDef = z.infer<typeof SkillDef>;

/** Редкость усиления: влияет на вес в пуле «1 из 3». */
export const BoostRarity = z.enum(["common", "rare", "epic"]);
export type BoostRarity = z.infer<typeof BoostRarity>;

/** Усиление «1 из 3»: живёт до проигрыша или выхода из забежки. */
export const BoostDef = z.object({
  id: ContentId,
  name: I18nKey,
  description: I18nKey,
  rarity: BoostRarity,
  /** Вес в пуле (чем больше, тем чаще предлагается). */
  weight: PosInt,
  /**
   * Сколько раз можно взять за забежку; `null` — без потолка («бесконечное» усиление): в предложение попадает, только
   * когда обычный пул не набирает полного выбора.
   */
  maxStacks: PosInt.nullable(),
  /** Эффект одного взятия; стаки складываются. */
  modifiers: z.array(StatModifier).min(1),
  /** С какой волны может выпасть. */
  minWave: PosInt.default(1),
});
export type BoostDef = z.infer<typeof BoostDef>;

/** Улучшение штаба во вкладке Tech: прочность, пушка, склад (офлайн-доход). */
export const TechDef = z.object({
  id: ContentId,
  name: I18nKey,
  description: I18nKey,
  group: z.enum(["hq", "cannon", "storage", "base"]),
  maxLevel: PosInt,
  cost: CostCurve,
  /** Эффект одного уровня; уровни складываются. */
  modifiers: z.array(StatModifier),
  /** +секунд к потолку офлайн-дохода за уровень (только `storage`). */
  offlineCapSecondsPerLevel: UInt.default(0),
});
export type TechDef = z.infer<typeof TechDef>;
